import { useCallback, useState } from 'react';
import { PERMISSIONS, SUPER_ADMIN_ONLY, type PermissionKey } from '@london-life/auth';
import { Badge } from '@london-life/ui/components/badge';
import { Button } from '@london-life/ui/components/button';
import { Input } from '@london-life/ui/components/input';
import { Label } from '@london-life/ui/components/label';
import { CheckList } from '@london-life/ui/admin/check-list';
import { DataTable } from '@london-life/ui/admin/data-table';
import { FormDialog } from '@london-life/ui/admin/form-dialog';
import { PageHeader } from '@london-life/ui/admin/page-header';
import { ErrorNotice } from '@london-life/ui/feedback/error-notice';
import { api } from '../lib/api';
import { useRequest } from '../lib/use-request';

// every permission a role may hold, described in words; super-admin-only permissions are never offered
const PERMISSION_OPTIONS = (Object.keys(PERMISSIONS) as PermissionKey[])
  .filter((key) => !SUPER_ADMIN_ONLY.includes(key))
  .map((key) => ({ value: key, label: key, hint: PERMISSIONS[key] }));
const EMPTY_DRAFT = { key: '', name: '', description: '', permissions: [] as string[] };

// roles are named sets of permissions; admins get roles, never single permissions
export function RolesPage() {
  const roles = useRequest(useCallback(() => api.admin.listRoles(), []));
  const [draft, setDraft] = useState<typeof EMPTY_DRAFT | null>(null);
  const [editing, setEditing] = useState<{ role: { id: string; name: string }; permissions: string[] } | null>(null);

  return (
    <section>
      <PageHeader
        title="Roles"
        description="Named sets of permissions. Changing a role signs out every admin who holds it."
        actions={<Button onClick={() => setDraft(EMPTY_DRAFT)}>New role</Button>}
      />
      {roles.error && <ErrorNotice error={roles.error} onRetry={() => void roles.reload()} />}
      <DataTable
        rows={roles.data?.roles ?? null}
        loading={roles.busy}
        rowKey={(role) => role.id}
        columns={[
          {
            key: 'name',
            header: 'Role',
            render: (role) => (
              <>
                <strong>{role.name}</strong> {role.system && <Badge variant="outline">System</Badge>}
                <div className="admin-muted">{role.description}</div>
              </>
            ),
          },
          {
            key: 'permissions',
            header: 'Permissions',
            render: (role) => (
              <div className="admin-tags">
                {role.permissions.map((permission) => (
                  <Badge key={permission} variant="secondary" title={PERMISSIONS[permission as PermissionKey]}>
                    {permission}
                  </Badge>
                ))}
              </div>
            ),
          },
          {
            key: 'actions',
            header: '',
            className: 'text-right',
            render: (role) => (
              <Button size="sm" variant="outline" onClick={() => setEditing({ role, permissions: role.permissions })}>
                Edit
              </Button>
            ),
          },
        ]}
      />

      <FormDialog
        open={draft !== null}
        title="New role"
        submitLabel="Create role"
        canSubmit={Boolean(draft?.key && draft.name && draft.permissions.length)}
        onClose={() => setDraft(null)}
        onSubmit={async () => {
          if (!draft) return;
          await api.admin.createRole(draft);
          await roles.reload();
        }}
      >
        {draft && (
          <>
            <div className="admin-field">
              <Label htmlFor="role-name">Name</Label>
              <Input
                id="role-name"
                value={draft.name}
                onChange={(event) => setDraft({ ...draft, name: event.target.value })}
                required
              />
            </div>
            <div className="admin-field">
              <Label htmlFor="role-key">Key</Label>
              <Input
                id="role-key"
                value={draft.key}
                placeholder="for example content_reviewer"
                pattern="[a-z][a-z0-9_]*"
                onChange={(event) => setDraft({ ...draft, key: event.target.value })}
                required
              />
            </div>
            <div className="admin-field">
              <Label htmlFor="role-description">Description</Label>
              <Input
                id="role-description"
                value={draft.description}
                onChange={(event) => setDraft({ ...draft, description: event.target.value })}
              />
            </div>
            <div className="admin-field">
              <Label>Permissions</Label>
              <CheckList
                options={PERMISSION_OPTIONS}
                selected={draft.permissions}
                onChange={(permissions) => setDraft({ ...draft, permissions })}
              />
            </div>
          </>
        )}
      </FormDialog>

      <FormDialog
        open={editing !== null}
        title={`Permissions for ${editing?.role.name ?? ''}`}
        description="Saving signs out every admin with this role, so the change applies straight away."
        submitLabel="Save permissions"
        canSubmit={Boolean(editing?.permissions.length)}
        onClose={() => setEditing(null)}
        onSubmit={async () => {
          if (!editing) return;
          await api.admin.updateRole(editing.role.id, { permissions: editing.permissions });
          await roles.reload();
        }}
      >
        <CheckList
          options={PERMISSION_OPTIONS}
          selected={editing?.permissions ?? []}
          onChange={(permissions) => editing && setEditing({ ...editing, permissions })}
        />
      </FormDialog>
    </section>
  );
}
